import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Progress } from '@bingads-webui-react/progress';

import { uploadPreviewStyle } from './styles/upload-assets-preview-style';

export const UploadPreviewWithCustomPreview = ({
  assetList,
  maxUploadNum,
  PreviewComponent,
  customClasses,
  showTitle,
}) => {
  const classes = useDefaultStyles(uploadPreviewStyle);
  const i18n = useContext(I18nContext);

  const getFileSize = useCallback((fileSize) => {
    let size = fileSize;
    let fileSizeStr = '';
    if ((fileSize / 1024) < 1024) {
      size = fileSize / 1024;
      fileSizeStr = i18n.getString(_TL_('{{fileSize}} KB'), { fileSize: size.toFixed(2) });
    } else {
      size = fileSize / 1024 / 1024;
      fileSizeStr = i18n.getString(_TL_('{{fileSize}} MB'), { fileSize: size.toFixed(2) });
    }

    return fileSizeStr;
  }, [i18n]);

  const normalInfoBox = item => (
    <div className={classnames('normal-info-box', customClasses.normalInfoBox)}>
      <div className={classes.progressBox}>
        <div className={classnames(classes.progressLine, 'progress-line')}>
          <Progress
            progressPercentage={item.progress}
          />
        </div>
        <div
          className={classnames(
            classes.statusIcon,
            'status-icon',
            item.progress === 1 ? `${classes.completeIcon} complete-icon` :
            `${classes.ongoingIcon} ongoing-icon`
          )}
        />
      </div>
      <div className={classnames(classes.imageSize, 'file-size')}>{getFileSize(item.size)}</div>
    </div>
  );

  const errorInfoBox = () => (
    <div className={classnames('error-info-box', customClasses.errorInfoBox)}>
      <div
        className={classnames(
            classes.statusIcon,
            classes.errorIcon,
            'status-icon',
            'error-icon'
          )}
      />
      <div className={classnames(classes.errorMsg, 'error-msg')}>{i18n.getString(_TL_('Upload failed. Maximum limit reached.'))}</div>
    </div>
  );

  const uploadTitle = i18n.getString(_TL_('Uploading {{uploadingNum}} files...'), { uploadingNum: maxUploadNum ? Math.min(maxUploadNum, assetList.length) : assetList.length });

  const previewComponentClasses = useMemo(() => ({
    imageContent: classnames(classes.imageContent, customClasses.imageContent),
    imageContentText: classnames(customClasses.imageContentText),
  }), [customClasses, classes]);

  return (
    assetList && assetList.length > 0 ?
      <div className={classnames('upload-preview-container', classes.uploadPreviewContainer)} aria-label={uploadTitle}>
        {
          showTitle && (
            <div className={classes.uploadTitle}>
              {uploadTitle}
            </div>
          )
        }
        <div className={classes.imagePreviewContainer}>
          {
            assetList.map((item, index) => {
              const exceedMaxUploadNum = maxUploadNum && index >= maxUploadNum;
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { progress, ...rest } = item;

              return (
                <div
                  className={classnames(
                    customClasses.imageItemBox || classes.imageItemBox,
                    (index + 1) % 3 === 0 ? classes.imageItemBoxRight : '',
                    exceedMaxUploadNum ? classes.imageItemErrBox : ''
                    )}
                  key={item.name}
                >
                  <PreviewComponent
                    className={previewComponentClasses.imageContent}
                    alt={i18n.getString(_TL_('Uploading file'))}
                    {...rest}
                  />
                  <div className={classnames(classes.imageName, customClasses.imageName)}>{item.name}</div>
                  {
                    exceedMaxUploadNum ? errorInfoBox() : normalInfoBox(item)
                  }
                </div>
              );
            })
          }
        </div>
      </div> : null
  );
};
UploadPreviewWithCustomPreview.propTypes = {
  assetList: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    progress: PropTypes.number,
    name: PropTypes.string,
    size: PropTypes.number,
  })).isRequired,
  maxUploadNum: PropTypes.number,
  PreviewComponent: PropTypes.func.isRequired,
  customClasses: PropTypes.shape({}),
  showTitle: PropTypes.bool,
};

UploadPreviewWithCustomPreview.defaultProps = {
  customClasses: {},
  showTitle: true,
  maxUploadNum: undefined,
};

export const SimpleImagePreview = ({ className, url, alt }) => (
  <img className={className} src={url} alt={alt} />
);

SimpleImagePreview.propTypes = {
  className: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
};

export const UploadPreview = props => (
  <UploadPreviewWithCustomPreview
    {...props}
    PreviewComponent={SimpleImagePreview}
  />
);
